import React from 'react'

import { Typography } from '@grammarly/badger-ui'

import Plagiarism from '../helpers/plagiarism-helper'
import { getPlagiarismTextBeginning } from './plagiarism-text'

import styles from './chart-description.module.scss'
import { ReportSummaryIconPrecise } from '../icons'

interface PlagiarismChartDescriptionProps {
  totalErrors: number
  plagiarism: Plagiarism
}

function getTextIssues(countErrors: number) {
  return `issue${countErrors === 1 ? '' : 's'}`
}

const { C3, TXL, TM } = Typography

export const PlagiarismChartDescription: React.FC<PlagiarismChartDescriptionProps> = ({ totalErrors, plagiarism }) => {
  const issueText = getTextIssues(totalErrors)
  const plagiarismTextBeginning = getPlagiarismTextBeginning(plagiarism)

  return (
    <C3 as="div" data-component-name="plagiarism-chart-description">
      {totalErrors > 0 ? (
        <>
          {plagiarismTextBeginning} {totalErrors} writing {issueText}.
        </>
      ) : (
        <>
          Your text is free of writing issues
          {plagiarism.isPresent && (
            <>
              , but{' '}
              <span className={styles.chartDescriptionBoldText}>
                we have found {plagiarism.isSignificant ? 'significant ' : ''}plagiarism
              </span>{' '}
              in your text
            </>
          )}
          .
        </>
      )}
    </C3>
  )
}

export const PlagiarismChartDescriptionPrecise: React.FC<PlagiarismChartDescriptionProps> = ({
  totalErrors,
  plagiarism,
}) => {
  const isPlagiarismPresent = plagiarism.isPresent
  const issueText = getTextIssues(totalErrors)
  const hasErrors = !!totalErrors

  return (
    <div className={styles.chartHeaderContainer}>
      <span className={styles.chartIconAndHeader}>
        <ReportSummaryIconPrecise hasErrors={hasErrors} plagiarism={plagiarism} location="inline" />
        <TXL as="span" className={styles.chartDescriptionBoldText}>
          {isPlagiarismPresent ? (
            <>Plagiarism{hasErrors ? ' and more' : ''} detected</>
          ) : (
            <>No plagiarism detected{hasErrors ? ', but...' : ''}</>
          )}
        </TXL>
      </span>
      <TM as="span">
        {!isPlagiarismPresent && !hasErrors ? (
          'Your text is free of writing issues.'
        ) : (
          <>
            We found{' '}
            <span className={hasErrors ? styles.chartDescriptionBoldText : ''}>{hasErrors ? totalErrors : 'no'}</span>
            {isPlagiarismPresent ? ' additional' : ''} writing {issueText} in your text.
          </>
        )}
      </TM>
    </div>
  )
}

export const PlagiarismChartDescriptionCopy: React.FC<PlagiarismChartDescriptionProps> = ({
  totalErrors,
  plagiarism,
}) => {
  const issueText = getTextIssues(totalErrors)
  const plagiarismTextBeginning = getPlagiarismTextBeginning(plagiarism)

  return (
    <>
      {totalErrors > 0 ? (
        <>
          {plagiarismTextBeginning} {totalErrors} writing {issueText}.
        </>
      ) : (
        <>
          Your text is free of writing issues
          {plagiarism.isPresent && (
            <>
              , but{' '}
              <span className={styles.chartDescriptionBoldText}>
                we have found {plagiarism.isSignificant ? 'significant ' : ''}plagiarism
              </span>{' '}
              in your text
            </>
          )}
          .
        </>
      )}
    </>
  )
}
